import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import BackButton from '@perpay-web/components/base/BackButton/BackButton';
import BottomSheet from '@perpay-web/components/base/BottomSheet/BottomSheet';
import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import {
    getData,
    getErrors,
    getIsComplete,
    getIsLoading,
    useDataModule,
} from '@perpay-web/data-module/useDataModule';
import { AddBankAccountForm } from '@perpay-web/fintech/components/composite/AddBankAccountForm/AddBankAccountForm';
import { ConfirmBankAccountForm } from '@perpay-web/fintech/components/composite/ConfirmBankAccountForm/ConfirmBankAccountForm';
import ProfileMFAContainer from '@perpay-web/fintech/components/containers/ProfileMFAContainer';
import { BANKS_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import { UseProfileMFAContextProvider } from '@perpay-web/fintech/hooks/useProfileMFAContext';

import styles from './AddBankAccountFlow.scss';

const STEPS = {
    ADD: 'ADD',
    CONFIRM: 'CONFIRM',
    MFA: 'MFA',
};

/**
 * @typedef {Object} ValidatedBankData
 * @property {string} accountNumber
 * @property {string} routingNumber
 * @property {string} bankName
 */

const AddBankAccountFlow = ({
    onClose,
    onSuccess,
    onFailure,
    userInfo,
    dispatchSendMFACode,
    dispatchAddBankAccountFlowViewed,
    dispatchConfirmBankAccountFlowViewed,
    dispatchAddBankAccountOTPViewed,
    dispatchConfirmBankAccountFlowSuccess,
    dispatchFetchUserInfo,
    buttonTheme,
    skipMFA,
}) => {
    const { dataRequest: createBank, state } = useDataModule((data) =>
        authenticatedJsonFetch(BANKS_ENDPOINT, data, HTTPMethod.post),
    );
    const isLoading = getIsLoading(state);
    const isComplete = getIsComplete(state);
    const errors = getErrors(state);
    const data = getData(state);

    useEffect(() => {
        dispatchAddBankAccountFlowViewed();
    }, [dispatchAddBankAccountFlowViewed]);

    useEffect(() => {
        dispatchFetchUserInfo();
    }, [dispatchFetchUserInfo]);

    useEffect(() => {
        if (isComplete && data) {
            onSuccess(data);
        }
    }, [isComplete, data, onSuccess]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            onFailure();
        }
    }, [errors, onFailure]);
    const [step, setStep] = useState(STEPS.ADD);
    /**
     * @type {[ValidatedBankData | null, React.Dispatch<React.SetStateAction<ValidatedBankData | null>>]}
     */
    const [validatedBankData, setValidatedBankData] = useState(null);
    const [finalFormData, setFinalFormData] = useState(null);

    const goBackToAdd = useCallback(() => {
        setStep(STEPS.ADD);
    }, []);

    const goToMfa = useCallback(() => {
        dispatchAddBankAccountOTPViewed();
        setStep(STEPS.MFA);
        dispatchSendMFACode();
    }, [dispatchSendMFACode, dispatchAddBankAccountOTPViewed]);

    const handleValidationSuccess = useCallback(
        (/** @type {ValidatedBankData} */ validatedData) => {
            dispatchConfirmBankAccountFlowViewed();
            setValidatedBankData(validatedData);
            setStep(STEPS.CONFIRM);
        },
        [dispatchConfirmBankAccountFlowViewed],
    );

    const userInfoMemo = useMemo(() => userInfo, [userInfo]);

    const handleSubmitWithLoading = useCallback(
        (finalData) => {
            createBank({
                account: finalData.accountNumber,
                routing: finalData.routingNumber,
                nickname: finalData.bankName,
                fullName: userInfoMemo ? userInfoMemo.fullName : '',
            });
        },
        [createBank, userInfoMemo],
    );

    const handleConfirmationSuccess = useCallback(
        (finalData) => {
            dispatchConfirmBankAccountFlowSuccess();
            setFinalFormData(finalData);
            if (skipMFA) {
                handleSubmitWithLoading(finalData);
            } else {
                goToMfa();
            }
        },
        [
            goToMfa,
            dispatchConfirmBankAccountFlowSuccess,
            skipMFA,
            handleSubmitWithLoading,
        ],
    );

    const MFAbackButtonElement = useMemo(() => {
        if (step === STEPS.MFA) {
            return <BackButton onClick={() => setStep(STEPS.CONFIRM)} />;
        }
        return null;
    }, [step]);

    return (
        <BottomSheet
            onClose={onClose}
            aria-label='Add Bank Account'
            contentClassName={styles['full-width']}
        >
            {step === STEPS.ADD ? (
                <AddBankAccountForm
                    onValidationSuccess={handleValidationSuccess}
                    buttonTheme={buttonTheme}
                    totalSteps={skipMFA ? 2 : 3}
                />
            ) : null}

            {step === STEPS.CONFIRM && validatedBankData ? (
                <ConfirmBankAccountForm
                    validatedData={validatedBankData}
                    onConfirmationSuccess={handleConfirmationSuccess}
                    onBack={goBackToAdd}
                    buttonTheme={buttonTheme}
                    loading={isLoading}
                    totalSteps={skipMFA ? 2 : 3}
                />
            ) : null}

            {step === STEPS.MFA && finalFormData ? (
                <UseProfileMFAContextProvider>
                    <ProfileMFAContainer
                        userInfo={userInfoMemo}
                        hideDefaultHeaderOnMobile
                        headerCurrentStep={3}
                        headerSteps={3}
                        headerRightButtonElement={null}
                        headerLeftButtonElement={MFAbackButtonElement}
                        goBackPath={null}
                        onSuccess={() => handleSubmitWithLoading(finalFormData)}
                        loadingOverride={isLoading}
                    />
                </UseProfileMFAContextProvider>
            ) : null}
        </BottomSheet>
    );
};

export default AddBankAccountFlow;
